import ReactGA from "react-ga4";

const itIsProductionOrStagingEnv =
  process.env.NODE_ENV === "production" || process.env.NODE_ENV === "staging";

/**
 * Configures GA4 for gaEvent(), window.gtag events, user_id and client_id.
 *
 * It does not send a page_view: the Google tag in GTM configures the same
 * measurement ID and already sends one per page load, so this config sending
 * its own doubled every page load. Page changes inside the app are still sent
 * once, by GA4 enhanced measurement.
 *
 * @param {string} measurementId
 * @returns {void}
 */
export function initGoogleAnalytics(measurementId) {
  ReactGA.initialize(measurementId, {
    gaOptions: {
      allowLinker: true,
      cookieDomain: "auto"
    },
    gtagOptions: {
      send_page_view: false
    }
  });
  ReactGA.ga("require", "linker");
  ReactGA.ga("linker:autoLink", ["shopify.com", "shopify.ca"]);
}

export function gaEvent({ category, action, label, options = {} }) {
  try {
    if (itIsProductionOrStagingEnv) {
      ReactGA.event({ category, action, label, options });
    }
  } catch (err) {
    console.log(err);
  }
}

export function triggerBingAdsEvent(eventName, eventData) {
  if (typeof window === "undefined") {
    return;
  }

  window.uetq = window.uetq || [];
  window.uetq.push("event", eventName, eventData);
}

/**
 * Retrieves the GA4 client_id from the user's browser
 * @returns {Promise<string|null>} GA4 client_id or null if not available
 */
export function getGA4ClientId() {
  return new Promise(resolve => {
    if (typeof window === "undefined") {
      resolve(null);
      return;
    }

    const measurementId = process.env.REACT_APP_GA_KEY; // G-D8XPV32TRB

    // Method 1: Via gtag (most reliable)
    if (typeof window.gtag !== "undefined" && measurementId) {
      window.gtag("get", measurementId, "client_id", clientId => {
        resolve(clientId || null);
      });
    }
    // Method 2: Via _ga cookie (fallback)
    else {
      const cookies = document.cookie.split(";");
      const gaCookie = cookies.find(c => c.trim().startsWith("_ga="));

      if (gaCookie) {
        // Format: _ga=GA1.2.1234567890.1234567890
        const parts = gaCookie.split("=")[1]?.split(".");
        if (parts && parts.length >= 4) {
          const clientId = `${parts[2]}.${parts[3]}`;
          resolve(clientId);
        }
      }

      resolve(null);
    }
  });
}

/**
 * Retrieves the GA4 session_id from the user's browser
 * @returns {string|null} GA4 session_id or null if not available
 */
export function getGA4SessionId() {
  if (typeof window === "undefined") {
    return null;
  }

  const cookies = document.cookie.split(";");

  // Try _ga4_session_id first (GA4)
  const sessionCookie = cookies.find(c => c.trim().startsWith("_ga4_session_id="));

  if (sessionCookie) {
    return sessionCookie.split("=")[1];
  }

  // Fallback: try _ga_session (GA4 alternative)
  const gaSessionCookie = cookies.find(c => c.trim().startsWith("_ga_session="));

  if (gaSessionCookie) {
    return gaSessionCookie.split("=")[1];
  }

  return null;
}

/**
 * Retrieves both client_id and session_id from GA4
 * @returns {Promise<{ga4_client_id: string|null, ga4_session_id: string|null}>}
 */
export async function getGA4Identifiers() {
  const [clientId, sessionId] = await Promise.all([getGA4ClientId(), Promise.resolve(getGA4SessionId())]);

  return {
    ga4_client_id: clientId,
    ga4_session_id: sessionId
  };
}
